import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Card } from '../components/Common';
import { usePollRespondInfo, usePollSharedRespondInfo, useSubmitPollResponse } from '../hooks/usePolls';

type ResponseValue = 'in' | 'out';

export function RespondToPoll() {
  const { pollToken, responseToken } = useParams<{ pollToken: string; responseToken?: string }>();
  const isSharedLink = !responseToken;

  const sharedInfo = usePollSharedRespondInfo(isSharedLink ? pollToken : undefined);
  const respondInfo = usePollRespondInfo(!isSharedLink ? pollToken : undefined, responseToken);
  const submitResponse = useSubmitPollResponse();

  const [selectedResponseToken, setSelectedResponseToken] = useState<string>('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submittedResponse, setSubmittedResponse] = useState<ResponseValue | null>(null);

  const sharedPhysios = sharedInfo.data?.physios || [];

  const selectedPhysio = useMemo(() => {
    if (!isSharedLink) return null;
    return sharedPhysios.find((p) => p.response_token === selectedResponseToken) || null;
  }, [isSharedLink, sharedPhysios, selectedResponseToken]);

  const message = isSharedLink ? sharedInfo.data?.message : respondInfo.data?.message;
  const existingResponse = isSharedLink ? selectedPhysio?.response : respondInfo.data?.response;

  const handleSubmit = async (response: ResponseValue) => {
    if (!pollToken) return;
    const tokenToUse = isSharedLink ? selectedResponseToken : responseToken;
    if (!tokenToUse) {
      setSubmitError('Select your name first.');
      return;
    }

    setSubmitError(null);
    try {
      await submitResponse.mutateAsync({
        pollToken,
        responseToken: tokenToUse,
        data: { response },
      });
      setSubmittedResponse(response);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to submit response';
      setSubmitError(message);
    }
  };

  if (!pollToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card>
          <p className="text-sm text-gray-600">Invalid response link.</p>
        </Card>
      </div>
    );
  }

  const isLoading = isSharedLink ? sharedInfo.isLoading : respondInfo.isLoading;
  const hasError = isSharedLink ? sharedInfo.error : respondInfo.error;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card>
          <div className="space-y-4 text-center">
            <h1 className="text-xl font-semibold text-gray-900">Availability Check</h1>

            {isLoading && <p className="text-sm text-gray-500">Loading...</p>}
            {hasError && <p className="text-sm text-red-600">Unable to load poll.</p>}

            {!isLoading && !hasError && (
              <>
                <p className="text-sm text-gray-700">
                  {message || 'Can you cover this appointment?'}
                </p>

                {isSharedLink && (
                  <div className="text-left">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Your name
                    </label>
                    <select
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                      value={selectedResponseToken}
                      onChange={(e) => {
                        setSelectedResponseToken(e.target.value);
                        setSubmittedResponse(null);
                        setSubmitError(null);
                      }}
                    >
                      <option value="">Select your name...</option>
                      {sharedPhysios.map((physio) => (
                        <option key={physio.physio_id} value={physio.response_token}>
                          {physio.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {(submittedResponse || existingResponse) && (
                  <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">
                    {submittedResponse
                      ? `Thanks, you are ${submittedResponse === 'in' ? 'in' : 'out'}.`
                      : existingResponse === 'in'
                        ? "You've already responded: In."
                        : "You've already responded: Out."}
                  </p>
                )}

                {submitError && (
                  <p className="text-sm text-red-600" role="alert">
                    {submitError}
                  </p>
                )}

                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  <Button
                    onClick={() => handleSubmit('in')}
                    disabled={submitResponse.isPending || (isSharedLink && !selectedResponseToken)}
                  >
                    I&apos;m in
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => handleSubmit('out')}
                    disabled={submitResponse.isPending || (isSharedLink && !selectedResponseToken)}
                  >
                    I&apos;m out
                  </Button>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
